import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MyWorkHistoryItem } from '@app/core/types/work/work.types';
import { DestroyableDirective } from '@app/shared/directives/destroyable.directive';
import { AnimationController } from '@ionic/angular';
import { WorkSandbox } from '../../sandboxes/work.sandbox';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { faCheck, faHourglass } from '@fortawesome/pro-solid-svg-icons';

const ICON_HEIGHT = 18;
const DURATION = 500;

@Component({
    selector: 'c-work-week-overview',
    templateUrl: './work-week-overview.component.html',
    styleUrls: ['./work-week-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class WorkWeekOverviewComponent extends DestroyableDirective implements AfterViewInit {
  @Input() historyItems: MyWorkHistoryItem[];
  @Input() loading: boolean;
  @Output() navigateToWeek: EventEmitter<MyWorkHistoryItem> = new EventEmitter();

  chartVisible = false;
  showNextWeek = false;
  showPreviousWeek = true;

  leftIcon = faChevronLeft;
  rightIcon = faChevronRight;
  faCheck = faCheck;
  faHourglass = faHourglass;

  @ViewChild('dashboardChart', { read: ElementRef, static: false }) dashboardChart: ElementRef;
  @ViewChildren('bars', { read: ElementRef }) bars: QueryList<ElementRef>;
  @ViewChildren('icons', { read: ElementRef }) icons: QueryList<ElementRef>;
  @ViewChildren('labels', { read: ElementRef }) labels: QueryList<ElementRef>;
  @ViewChildren('chartColumns') chartColumns: QueryList<ElementRef>;

  constructor(private animationController: AnimationController, private workSandbox: WorkSandbox) {
    super();
  }

  ngAfterViewInit(): void {
    this.workSandbox.scrollTrigger$.pipe(this.takeUntilDestroyed()).subscribe(() => {
      this.checkIfChartIsVisibleAndAnimate();
    });
  }

  get hasWorkHistoryItems(): boolean {
    return !!this.historyItems?.find((historyItem) => (historyItem?.workedHours ?? 0) > 0);
  }

  get visibleHistoryItems(): MyWorkHistoryItem[] {
    return this.historyItems ?? [];
  }

  onScroll(): void {
    const containerRect = this.dashboardChart.nativeElement.getBoundingClientRect();
    const first = this.chartColumns.toArray()[this.chartColumns.toArray().length - 1];
    const last = this.chartColumns.toArray()[0];

    if (!this.dashboardChart || !last) {
      this.showNextWeek = false;
    }
    const lastColumnRect = last.nativeElement.getBoundingClientRect();
    this.showNextWeek = Math.round(containerRect.right) !== Math.round(lastColumnRect.right);

    if (!this.dashboardChart || !first) {
      this.showPreviousWeek = false;
    }
    const firstColumnRect = first.nativeElement.getBoundingClientRect();
    this.showPreviousWeek = Math.round(containerRect.left) !== Math.round(firstColumnRect.left);
  }

  setNextWeek(): void {
    this.dashboardChart.nativeElement.scrollLeft += this.dashboardChart.nativeElement.clientWidth;
  }

  setPreviousWeek(): void {
    this.dashboardChart.nativeElement.scrollLeft -= this.dashboardChart.nativeElement.clientWidth;
  }

  calculateHeight(workedHours: number): number {
    const maxWorkedHoursInSelectedWeek = Math.max(...this.visibleHistoryItems.map((item) => item.workedHours));
    return 122 * (workedHours / maxWorkedHoursInSelectedWeek);
  }

  viewWeekDetails(historyItem: MyWorkHistoryItem): void {
    this.navigateToWeek.emit(historyItem);
  }

  checkIfChartIsVisibleAndAnimate(): void {
    if (this.chartVisible) {
      return;
    }

    //check if chart is fully visible in viewport(-60 to account for height of navigation)
    const rect = this.dashboardChart?.nativeElement?.getBoundingClientRect();
    if (!rect) {
      return;
    }
    //check if chart is fully visible in viewport(-80 to account for height of navigation)
    const topShown = rect.top >= 0;
    const bottomShown = rect.bottom <= window.innerHeight - 60;
    if (topShown && bottomShown) {
      this.chartVisible = true;
      for (let i = 0; i < this.dashboardChart.nativeElement.children.length; i++) {
        const calculatedHeight = this.calculateHeight(this.visibleHistoryItems[i].workedHours);
        this.animationController
          .create()
          .addElement(this.bars.toArray()[i].nativeElement)
          .duration(DURATION)
          .iterations(1)
          .fromTo('height', '0', calculatedHeight + 'px')
          .play();

        //only show icons if chart pipe is tall enough
        if (calculatedHeight > ICON_HEIGHT) {
          this.animationController
            .create()
            .addElement([this.labels.toArray()[i].nativeElement, this.icons.toArray()[i].nativeElement])
            .delay(DURATION * 0.8)
            .duration(DURATION * 0.2)
            .iterations(1)
            .fromTo('opacity', '0', '1')
            .play();
          this.icons.toArray()[i].nativeElement.style.display = 'block';
        }

        this.labels.toArray()[i].nativeElement.style.display = 'block';
      }
    }
  }
}
